import type { ComponentProps } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'

import HotelSearchHeader from 'components/Pages/HotelSearchPage/HotelSearchHeader'
import useScrollDirection from 'hooks/useScrollDirection'
import zIndex from 'styles/tools/z-index'
import MetaSearchV2SearchBar from './MetaSearchV2SearchBar'
import MetaSearchV2SortRow from './MetaSearchV2SortRow'

type HotelSearchHeaderProps = ComponentProps<typeof HotelSearchHeader>
type FilterRowProps = ComponentProps<typeof MetaSearchV2SortRow>

type Props = HotelSearchHeaderProps & {
  filterRow: FilterRowProps
}

const Sentinel = styled.div`
  height: 0;
  width: 0;
`

/** Sticky so it stays in flow; must be a child of the tall page column. */
const StickyShell = styled.div`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickySubHeader};
  transform: translateY(0);
  transition: transform 0.25s ease-out;

  &.hidden {
    transform: translateY(-100%);
    pointer-events: none;
  }
`

/** Meta search mobile V2 header: search bar + filter/sort row. */
function MetaSearchV2Header({ filterRow, ...props }: Props) {
  const scrollDirection = useScrollDirection({ tolerance: 12 })
  const [sentinelRef, sentinelInView] = useInView({ initialInView: true })

  // Past the top + scrolling down → slide away; scrolling up → slide back.
  const isHidden = !sentinelInView && scrollDirection === 'down'

  return (
    <>
      <Sentinel ref={sentinelRef} />
      <StickyShell className={clsx({ hidden: isHidden })}>
        <HotelSearchHeader
          {...props}
          flat
          disableSticky
          compactSearchField={(onEdit) => (
            <>
              <MetaSearchV2SearchBar onEdit={onEdit} />
              <MetaSearchV2SortRow {...filterRow} />
            </>
          )}
        />
      </StickyShell>
    </>
  )
}

export default MetaSearchV2Header
